// ALT-PDP-SYSTEM-003 — keep a valid Size selected across Line changes.
//
// THE BUG, reproduced on the dev theme:
//   Signature + size 8  ->  switch to Comfort Fit  ->  switch back
//   result: no size checked, ?variant dropped from the URL, and the price
//   element carrying `hidden` while still showing the other line's $489.
//
// WHY: "Made to Measure (Miami)" is Comfort Fit's only Size and does not exist
// for Signature. Dawn carries the stale Size into the next request, that
// combination resolves to no variant, and product-info.js calls setUnavailable()
// and returns BEFORE publishing `variantChange` — so there is no event to hook.
// Hence a DOM watcher rather than pubsub.
//
// THREE THINGS THIS HAS TO GET RIGHT, each found the hard way:
//
//  1. `<variant-selects>` is REPLACED wholesale on every change (node identity
//     differs afterwards), so neither the observer nor the memory can live on
//     it. Both live on `<product-info>`, which performs the update and survives.
//
//  2. While the request is in flight the DOM reads "new line, old line's size".
//     Recording there wrote Comfort Fit's "Made to Measure (Miami)" into
//     Signature's slot, so the restore looked for a size Signature does not
//     have and fell through to the first one. Nothing is recorded or repaired
//     until the picker has ACTUALLY been rebuilt, detected by that same
//     node-identity change.
//
//  3. On an unresolved combination the server re-renders with NOTHING checked,
//     including the Line radio — so reading the line from the DOM at repair
//     time returns '' and the lookup misses. The line the customer clicked is
//     kept as the fallback key.
//
// Behaviour (owner-specified): returning to a line that had a size restores
// that size; a line that never had one gets the first.
//
// Progressive enhancement. Without JS the picker behaves exactly as today. It
// supports both a radio group and the PDP's Signature size dropdown.
if (!window.__altPdpSizeMemoryInit) {
  window.__altPdpSizeMemoryInit = true;

  const optionName = (input) => (input?.dataset?.optionName || '').trim().toLowerCase();

  const attach = (host) => {
    if (!host || host.__altSizeMemory) return;
    host.__altSizeMemory = true;

    const remembered = new Map();
    host.__altSizeMemoryMap = remembered; // inspectable during QA

    let settling = false;
    let repairTimer = null;
    let intendedLine = null;

    const controlsFor = (name) =>
      [...host.querySelectorAll('variant-selects input[data-option-name], variant-selects select')].filter((control) => {
        if (control.tagName === 'SELECT') {
          return optionName(control.selectedOptions[0] || {}) === name;
        }
        return optionName(control) === name;
      });

    const isSelected = (control) =>
      control.tagName === 'SELECT' ? control.selectedOptions.length > 0 : control.checked;

    const isPickable = (control) => {
      if (control.tagName === 'SELECT') {
        return !control.disabled;
      }
      return !control.disabled && !control.classList.contains('disabled');
    };

    // Dawn already publishes the variant it actually resolved, as JSON inside
    // the picker it just rendered. That is the only trustworthy signal that a
    // (line, size) pair is real: the radios alone are not, because the Line
    // radio flips client-side the instant it is clicked while the Size list
    // still belongs to the previous line. Every cheaper guard tried before this
    // one — key matching, node identity, debouncing — recorded that phantom
    // pair and poisoned the memory.
    const resolvedOptions = () => {
      const el = host.querySelector('variant-selects script[data-selected-variant]');
      if (!el) return null;
      try {
        const variant = JSON.parse(el.textContent);
        return variant && Array.isArray(variant.options) ? variant.options : null;
      } catch (error) {
        return null;
      }
    };

    const repair = () => {
      if (settling) return;
      const sizes = controlsFor('size');
      if (!sizes.length) return;

      const line = controlsFor('line').find(isSelected)?.value || intendedLine || '';
      const wanted = line ? remembered.get(line) : null;

      const sizeSelect = sizes.find((control) => control.tagName === 'SELECT');
      if (sizeSelect) {
        const wantedOption = wanted && [...sizeSelect.options].find((option) => option.value === wanted && !option.disabled);
        if (wantedOption && sizeSelect.value !== wantedOption.value) {
          settling = true;
          sizeSelect.value = wantedOption.value;
          sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          setTimeout(() => {
            settling = false;
          }, 1200);
        }
        return;
      }

      if (sizes.some(isSelected)) return;

      const target = (wanted && sizes.find((control) => control.value === wanted && isPickable(control))) || sizes.find(isPickable);
      if (!target) return;

      // One extra section render, and only on the transition that was broken.
      settling = true;
      target.click();
      setTimeout(() => {
        settling = false;
      }, 1200);
    };

    const sync = () => {
      const sizes = controlsFor('size');
      if (!sizes.length) return;

      const checked = sizes.find(isSelected);
      const domLine = controlsFor('line').find(isSelected)?.value || intendedLine || '';

      if (checked) {
        const wanted = domLine ? remembered.get(domLine) : null;
        if (checked.tagName === 'SELECT' && wanted && checked.value !== wanted) {
          const wantedOption = [...checked.options].find((option) => option.value === wanted && !option.disabled);
          if (wantedOption) {
            repair();
            return;
          }
        }

        // Only record a pair the server confirmed. See resolvedOptions().
        const options = resolvedOptions();
        if (domLine && options && options.includes(domLine) && options.includes(checked.value)) {
          remembered.set(domLine, checked.value);
        }
        return;
      }

      clearTimeout(repairTimer);
      repairTimer = setTimeout(repair, 200);
    };

    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        sync();
      });
    }).observe(host, { childList: true, subtree: true });

    host.addEventListener('change', (event) => {
      const input = event.target;
      if (input && input.dataset && optionName(input) === 'line' && input.checked) {
        intendedLine = input.value;
      }
      requestAnimationFrame(sync);
    });

    sync();
  };

  // Attach to every product-info on the page, including those inside the
  // cart quick view. The same per-Line size memory logic applies everywhere
  // a variant-selects picker is rendered inside a product-info element.
  const boot = () => {
    document.querySelectorAll('product-info').forEach(attach);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
